import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Mail, ChevronDown, Link2, QrCode, BarChart3, User, Shield } from 'lucide-react';
import { DashboardLayout } from '@/components/layout';
import { Card, CardTitle, Button } from '@/components/ui';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

const FAQItem = ({ question, answer, isOpen, onToggle }: FAQItemProps) => {
  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-left hover:text-lime-600 transition-colors"
      >
        <span className="font-medium text-dark-800 dark:text-white pr-4">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="pb-4 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

const Help = () => {
  const { t } = useTranslation();
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);

  const faqCategories = [
    {
      id: 'deeplinks',
      icon: <Link2 className="w-5 h-5" />,
      title: t('help.faqDeeplinks'),
      color: 'lime',
      questions: [
        { id: 'what-is-deeplink', q: t('help.faqWhatIsDeeplink'), a: t('help.faqWhatIsDeeplinkAnswer') },
        { id: 'platforms', q: t('help.faqPlatforms'), a: t('help.faqPlatformsAnswer') },
        { id: 'how-works', q: t('help.faqHowWorks'), a: t('help.faqHowWorksAnswer') },
      ],
    },
    {
      id: 'profile',
      icon: <User className="w-5 h-5" />,
      title: t('help.faqProfile'),
      color: 'lavender',
      questions: [
        { id: 'what-is-profile', q: t('help.faqWhatIsProfile'), a: t('help.faqWhatIsProfileAnswer') },
        { id: 'widgets', q: t('help.faqWidgets'), a: t('help.faqWidgetsAnswer') },
      ],
    },
    {
      id: 'qr',
      icon: <QrCode className="w-5 h-5" />,
      title: t('help.faqQR'),
      color: 'blue',
      questions: [
        { id: 'qr-customize', q: t('help.faqQRCustomize'), a: t('help.faqQRCustomizeAnswer') },
        { id: 'qr-formats', q: t('help.faqQRFormats'), a: t('help.faqQRFormatsAnswer') },
      ],
    },
    {
      id: 'stats',
      icon: <BarChart3 className="w-5 h-5" />,
      title: t('help.faqStats'),
      color: 'orange',
      questions: [
        { id: 'stats-data', q: t('help.faqStatsData'), a: t('help.faqStatsDataAnswer') },
        { id: 'stats-realtime', q: t('help.faqStatsRealtime'), a: t('help.faqStatsRealtimeAnswer') },
      ],
    },
    {
      id: 'account',
      icon: <Shield className="w-5 h-5" />,
      title: t('help.faqAccount'),
      color: 'gray',
      questions: [
        { id: 'delete-account', q: t('help.faqDeleteAccount'), a: t('help.faqDeleteAccountAnswer') },
        { id: 'change-username', q: t('help.faqChangeUsername'), a: t('help.faqChangeUsernameAnswer') },
      ],
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      lime: 'bg-lime-100 text-lime-700',
      lavender: 'bg-lavender-100 text-lavender-700',
      blue: 'bg-blue-100 text-blue-700',
      orange: 'bg-orange-100 text-orange-700',
      gray: 'bg-gray-100 text-gray-700',
    };
    return colors[color] || colors.gray;
  };

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">{t('help.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400">{t('help.subtitle')}</p>
      </div>

      {/* Contact Card */}
      <Card variant="default" padding="lg" className="mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Mail className="w-6 h-6 text-lime-700" />
          </div>
          <div className="flex-1">
            <CardTitle className="mb-1">{t('help.needHelp')}</CardTitle>
            <p className="text-gray-500 text-sm">{t('help.needHelpDescription')}</p>
          </div>
          <Button
            variant="primary"
            size="md"
            leftIcon={<Mail className="w-4 h-4" />}
            onClick={() => window.location.href = 'mailto:contact@klipr.it'}
            className="flex-shrink-0"
          >
            {t('help.contactUs')}
          </Button>
        </div>
      </Card>

      {/* FAQ Section */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-6">
          <HelpCircle className="w-6 h-6 text-lime-600" />
          <h2 className="text-xl font-bold text-dark-800 dark:text-white">{t('help.faq')}</h2>
        </div>

        <div className="grid gap-6">
          {faqCategories.map((category) => (
            <Card key={category.id} variant="default" padding="lg">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getColorClasses(category.color)}`}>
                  {category.icon}
                </div>
                <CardTitle>{category.title}</CardTitle>
              </div>
              <div>
                {category.questions.map((faq) => (
                  <FAQItem
                    key={faq.id}
                    question={faq.q}
                    answer={faq.a}
                    isOpen={openFAQ === faq.id}
                    onToggle={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                  />
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Help;
